import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { renderPoster } from "@/lib/poster";

function dataUrlToFile(url, name) {
  const [meta, b64] = url.split(",");
  const mime = meta.match(/data:(.*?);/)[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], name, { type: mime });
}

export default function PosterModal({ stops, onClose }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  const fileName = `aetheria-expedition-${stops.length}-stops.png`;
  const tourUrl = `${window.location.origin}${window.location.pathname}#route=${stops.map((s) => s.id).join(",")}&tour=1`;
  const shareText = `My expedition through ${stops.length} questioned monuments — ${stops.map((s) => s.name).join(" → ")}. Walk it yourself:`;
  const canShareFiles =
    typeof navigator.canShare === "function" && src
      ? navigator.canShare({ files: [dataUrlToFile(src, fileName)] })
      : false;

  const nativeShare = async () => {
    try {
      const data = { title: "AETHERIA — Expedition", text: shareText, url: tourUrl };
      if (canShareFiles) data.files = [dataUrlToFile(src, fileName)];
      await navigator.share(data);
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing was cancelled or isn't available here");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(tourUrl);
      toast.success("Tour link copied — paste it anywhere");
    } catch {
      toast.error("Couldn't copy automatically");
    }
  };

  const enc = encodeURIComponent;
  const social = [
    { id: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(tourUrl)}` },
    { id: "whatsapp", label: "WHATSAPP", href: `https://wa.me/?text=${enc(`${shareText} ${tourUrl}`)}` },
    { id: "facebook", label: "FACEBOOK", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(tourUrl)}` },
    { id: "email", label: "EMAIL", href: `mailto:?subject=${enc("An expedition for you")}&body=${enc(`${shareText}\n\n${tourUrl}`)}` },
  ];

  useEffect(() => {
    let live = true;
    renderPoster(stops)
      .then((url) => live && setSrc(url))
      .catch(() => live && setErr(true));
    return () => {
      live = false;
    };
  }, [stops]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      data-testid="poster-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[85] flex items-center justify-center overflow-y-auto bg-[#0B0C10]/92 p-5 backdrop-blur-md"
      data-lenis-prevent
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[1100px]"
      >
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] text-[#D4AF37]">EXPEDITION POSTER</p>
            <h2 className="mt-2 font-serif text-2xl leading-tight sm:text-4xl">
              Your route, <em className="italic text-[#D4AF37]">printed in gold.</em>
            </h2>
          </div>
          <div className="flex gap-2">
            <a
              data-testid="poster-download"
              href={src || undefined}
              download={fileName}
              aria-disabled={!src}
              className={`border border-[#D4AF37] bg-[#D4AF37]/15 px-5 py-2.5 font-mono text-[9px] tracking-[0.25em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0B0C10] ${
                src ? "" : "pointer-events-none opacity-40"
              }`}
            >
              ↓ DOWNLOAD PNG
            </a>
            <button
              data-testid="poster-close"
              onClick={onClose}
              className="border border-white/20 px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-white/60 transition-colors hover:border-[#FF9F1C] hover:text-[#FF9F1C]"
            >
              CLOSE ×
            </button>
          </div>
        </div>
        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,520px)_1fr]">
          <div className="relative aspect-[12/17] w-full max-w-[520px] border border-[#D4AF37]/30 bg-[#12141C]">
            {src ? (
              <img data-testid="poster-image" src={src} alt="Expedition poster" className="h-full w-full" />
            ) : (
              <div className="flex h-full items-center justify-center font-mono text-[9px] tracking-[0.3em] text-white/40">
                {err ? "COULDN'T RENDER POSTER" : "PLOTTING THE ROUTE…"}
              </div>
            )}
          </div>
          <div className="font-light leading-relaxed text-white/60">
            <p className="text-sm sm:text-base">
              A 1200 × 1700 PNG of your expedition — the globe turned to face your route,
              gold great-circle arcs between every stop, and a tour link printed in the footer
              so anyone holding the poster can walk the same path.
            </p>
            <ol className="mt-6 space-y-2">
              {stops.map((s, i) => (
                <li key={s.id} className="flex items-baseline gap-3 font-serif text-base text-white/80">
                  <span className="font-mono text-[9px] text-[#D4AF37]/70">{String(i + 1).padStart(2, "0")}</span>
                  {s.name}
                </li>
              ))}
            </ol>

            <div data-testid="poster-share" className="mt-8 border-t border-white/10 pt-6">
              <p className="font-mono text-[9px] tracking-[0.35em] text-[#D4AF37]">SHARE THE EXPEDITION</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {typeof navigator.share === "function" && (
                  <button
                    data-testid="poster-share-native"
                    onClick={nativeShare}
                    disabled={!src}
                    className="border border-[#D4AF37] bg-[#D4AF37]/15 px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0B0C10] disabled:opacity-40"
                  >
                    ↗ SHARE{canShareFiles ? " POSTER" : ""}
                  </button>
                )}
                {social.map((s) => (
                  <a
                    key={s.id}
                    data-testid={`poster-share-${s.id}`}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="border border-white/20 px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-white/60 transition-colors hover:border-[#38BDF8] hover:text-[#38BDF8]"
                  >
                    {s.label}
                  </a>
                ))}
                <button
                  data-testid="poster-share-copy"
                  onClick={copyLink}
                  className="border border-white/20 px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-white/60 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
                >
                  COPY TOUR LINK
                </button>
              </div>
              <p className="mt-3 font-mono text-[8px] leading-relaxed tracking-[0.15em] text-white/30">
                Social buttons share the tour link; download the PNG to attach the poster itself.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
